import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { CallsScreen } from '../../pages/CallsScreen';
import { CallerExperienceScreen } from '../../pages/CallerExperienceScreen';
import { BottomTabBar } from '../ui/BottomTabBar';
import { DeckPhoneFrame } from './DeckPhoneFrame';
export type SimPhase = 'idle' | 'overview' | 'family' | 'voice' | 'vip' | 'done';
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface VexuCallerExperienceShowcaseProps {
  scale?: number;
  /** Changing this value restarts the guided simulation from the beginning. */
  runKey?: number;
  onPhaseChange?: (phase: SimPhase) => void;
  onComplete?: () => void;
}

/** Emergency handling is revealed later in the deck, so it stays out of view. */
const EMERGENCY_CALL_IDS = [12];

/**
 * Live product surface for the Caller Experience story. The real Calls screen
 * is the entry point and its own button opens the real Caller Experience
 * feature; the guided simulation drives those actual controls, so every state
 * shown is genuine product behaviour.
 */
export function VexuCallerExperienceShowcase({
  scale = 0.86,
  runKey = 0,
  onPhaseChange,
  onComplete
}: VexuCallerExperienceShowcaseProps) {
  const [screen, setScreen] = useState<'calls' | 'callerExperience'>('calls');
  const [focus, setFocus] = useState<Rect | null>(null);
  const [tap, setTap] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Indicators belong to the screen they were measured on — never carry them
  // across a screen change.
  useEffect(() => {
    setFocus(null);
    setTap(null);
  }, [screen]);
  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) => new Promise<void>(resolve => {
      timers.push(window.setTimeout(resolve, ms));
    });
    const find = (sim: string) => stageRef.current?.querySelector<HTMLElement>(`[data-sim="${sim}"]`) ?? null;
    const measure = (el: HTMLElement): Rect | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      const stageRect = stage.getBoundingClientRect();
      const k = stageRect.width / stage.offsetWidth || 1;
      const r = el.getBoundingClientRect();
      return {
        x: (r.left - stageRect.left) / k,
        y: (r.top - stageRect.top) / k,
        w: r.width / k,
        h: r.height / k
      };
    };

    /**
     * Scrolls the element into view WITHIN the phone only. `scrollIntoView`
     * would bubble up and scroll the scaled slide canvas out of the viewport.
     */
    const revealInPhone = (el: HTMLElement) => {
      const stage = stageRef.current;
      if (!stage) return;
      let node: HTMLElement | null = el.parentElement;
      while (node && node !== stage) {
        const style = window.getComputedStyle(node);
        const scrollable = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 4;
        if (scrollable) {
          const nodeRect = node.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          const k = nodeRect.height / node.clientHeight || 1;
          const delta = (elRect.top - nodeRect.top) / k - node.clientHeight / 2 + elRect.height / k / 2;
          node.scrollTo({
            top: node.scrollTop + delta,
            behavior: 'smooth'
          });
          return;
        }
        node = node.parentElement;
      }
    };

    /**
     * Highlights a real control. Indicators are always torn down before the
     * screen can change, so a marker never lands on the next screen.
     */
    const focusOn = async (sim: string, hold = 700, keep = false) => {
      const el = find(sim);
      if (!el || cancelled) return null;
      revealInPhone(el);
      await sleep(380);
      if (cancelled) return null;
      const rect = measure(el);
      if (rect) setFocus(rect);
      await sleep(hold);
      if (!keep) setFocus(null);
      return el;
    };
    const tapOn = async (sim: string) => {
      const el = await focusOn(sim, 300, true);
      if (!el || cancelled) return;
      const rect = measure(el);
      if (rect) setTap({
        x: rect.x + rect.w / 2,
        y: rect.y + rect.h / 2
      });
      await sleep(180);
      if (cancelled) return;
      // Clear the highlight and ripple FIRST — then navigate.
      setTap(null);
      setFocus(null);
      await sleep(140);
      if (cancelled) return;
      el.click();
      await sleep(520);
    };

    /** Waits until a control exists (or disappears) so screens never overlap. */
    const waitFor = async (sim: string, present: boolean, timeout = 2000) => {
      const deadline = Date.now() + timeout;
      while (!cancelled && Date.now() < deadline) {
        if (Boolean(find(sim)) === present) return true;
        await sleep(80);
      }
      return false;
    };
    const run = async () => {
      // Reset to the static entry state.
      setScreen('calls');
      setFocus(null);
      setTap(null);
      onPhaseChange?.('idle');
      await sleep(1300);
      if (cancelled) return;

      // Calls → Caller Experience
      await tapOn('caller-experience');
      if (cancelled) return;
      await sleep(700);
      onPhaseChange?.('overview');
      await sleep(600);

      // Family: the voice itself is configurable, then emergency + calendar
      await tapOn('group-family');
      onPhaseChange?.('family');
      if (cancelled) return;
      await sleep(600);

      // Voice: one clean tap into the real voice picker, then straight out.
      await tapOn('voice-family');
      if (cancelled) return;
      await waitFor('picker-close', true);
      if (cancelled) return;
      onPhaseChange?.('voice');
      await sleep(1500);
      if (cancelled) return;
      await tapOn('picker-close');
      if (cancelled) return;
      // The sheet must be fully gone before the next step begins.
      await waitFor('picker-close', false);
      if (cancelled) return;
      onPhaseChange?.('family');
      await sleep(450);
      await focusOn('emergency-family', 1500);
      if (cancelled) return;
      await tapOn('calendar-details-family');
      if (cancelled) return;
      await tapOn('perm-view-family');
      if (cancelled) return;
      await focusOn('availability-family', 1400);
      if (cancelled) return;
      await tapOn('group-family');
      if (cancelled) return;
      await sleep(400);

      // VIP Clients: calendar set to Book, with days, hours and slots
      await tapOn('group-vip');
      onPhaseChange?.('vip');
      if (cancelled) return;
      await sleep(400);
      await tapOn('calendar-details-vip');
      if (cancelled) return;
      await tapOn('perm-book_view-vip');
      if (cancelled) return;
      await focusOn('availability-vip', 2200);
      if (cancelled) return;
      await tapOn('group-vip');
      if (cancelled) return;

      // Back to the overview and a final pass across the three experiences
      onPhaseChange?.('overview');
      await sleep(500);
      await focusOn('group-family', 550);
      if (cancelled) return;
      await focusOn('group-vip', 550);
      if (cancelled) return;
      await focusOn('group-all', 650);
      if (cancelled) return;
      setFocus(null);
      onPhaseChange?.('done');
      onComplete?.();
    };
    run();
    return () => {
      cancelled = true;
      timers.forEach(id => window.clearTimeout(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  /**
   * The product screens are memoised so the presentation indicators (focus
   * ring, tap ripple) can update without re-rendering the real app — a
   * re-render there would remount its modals mid-animation.
   */
  const productSurface = useMemo(() => screen === 'calls' ? <>
          <CallsScreen onNavigate={next => {
      if (next === 'callerExperience') setScreen('callerExperience');
    }} initialCallId={-1} hiddenCallIds={EMERGENCY_CALL_IDS} showCommandComposer={false} data-id="element-291" data-mp="3pci38zkpkle9" />
          <BottomTabBar currentTab="calls" onTabChange={() => undefined} data-id="element-292" data-mp="kuf3t7hzg40w" />
        </> : <CallerExperienceScreen onBack={() => setScreen('calls')} isActivated seedExampleGroups data-id="element-293" data-mp="fxos0wd7nula" />, [screen]);
  return <ThemeProvider initialMode="light" data-id="element-294" data-mp="18luafcgsl7hy">
      <DeckPhoneFrame scale={scale} data-id="element-295" data-mp="18krjx5ndt72k">
        <div ref={stageRef} className="h-full relative" data-id="element-296" data-mp="13mq20o1ozdso">
          {productSurface}

          {/* Presentation layer: what is being tapped, and when */}
          <div className="pointer-events-none absolute inset-0 z-[70]" data-id="element-297" data-mp="yu6plueqqopg">
            <AnimatePresence data-id="element-298" data-mp="tvn23pki14uz">
              {focus && <motion.div key={`${focus.x}-${focus.y}-${focus.h}`} initial={{
              opacity: 0,
              scale: 1.04
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.12
              }
            }} transition={{
              duration: 0.28,
              ease: [0.22, 0.61, 0.36, 1]
            }} className="absolute rounded-[14px] ring-2 ring-vexu/70" style={{
              left: focus.x - 4,
              top: focus.y - 4,
              width: focus.w + 8,
              height: focus.h + 8,
              boxShadow: '0 0 0 3px rgba(0,200,83,0.12), 0 8px 28px -10px rgba(0,200,83,0.5)',
              background: 'rgba(0,200,83,0.06)'
            }} data-id="element-299" data-mp="1rc28hg5mx7ra" />}
            </AnimatePresence>

            <AnimatePresence data-id="element-300" data-mp="2mlxr33zvswrx">
              {tap && <motion.span key={`${tap.x}-${tap.y}`} className="absolute" style={{
              left: tap.x,
              top: tap.y
            }} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.1
              }
            }} transition={{
              duration: 0.12
            }} data-id="element-301" data-mp="2rksao85oapd3">
                  <motion.span className="absolute block rounded-full bg-vexu/25" style={{
                left: -22,
                top: -22,
                width: 44,
                height: 44
              }} initial={{
                scale: 0.4,
                opacity: 0.8
              }} animate={{
                scale: 1,
                opacity: 0
              }} transition={{
                duration: 0.32,
                ease: 'easeOut'
              }} data-id="element-302" data-mp="337k49a8pvnwe" />
                  <span className="absolute block rounded-full bg-vexu/80" style={{
                left: -7,
                top: -7,
                width: 14,
                height: 14,
                boxShadow: '0 0 0 3px rgba(255,255,255,0.75)'
              }} data-id="element-303" data-mp="3lyl21uy3pded" />
                </motion.span>}
            </AnimatePresence>
          </div>
        </div>
      </DeckPhoneFrame>
    </ThemeProvider>;
}